// Every league screen past the four places (My Team, Game Day, Players,
// League), in one list: the finder reads it, so every screen is found by name
// (B-402), and so does the League tab before a league's draft, as its doors
// (B-212). After the draft the screens are the front page's sections (B-403),
// and the rail lists the four places alone.

import type { IconName } from '../kit/Icon';

/** A door to a league screen, or to the chat drawer, which is not a page. */
export type LeagueDoor =
  | { kind: 'route'; label: string; icon: IconName; to: string }
  | { kind: 'chat'; label: string; icon: IconName };

/**
 * `real` is false in the tour's simulator, which has no door to a screen that
 * reads a real league's schedule (the planner).
 */
export function leagueDoors(
  base: string,
  setup: boolean,
  commissioner: boolean,
  real = true,
): LeagueDoor[] {
  const route = (label: string, icon: IconName, path: string): LeagueDoor => ({
    kind: 'route',
    label,
    icon,
    to: `${base}${path}`,
  });
  const settings = commissioner ? [route('Settings', 'settings', '/settings')] : [];
  const chat: LeagueDoor = { kind: 'chat', label: 'Chat', icon: 'chat' };
  if (setup) {
    return [
      route('Draft room', 'draft', '/draft'),
      route('Standings', 'standings', '/standings'),
      route('Rules', 'rules', '/rules'),
      // The seats' acts — a code rotated, a seat closed, a manager removed —
      // happen before the draft, so the log is a door here too.
      route('Commissioner log', 'veto', '/audit'),
      ...settings,
      chat,
    ];
  }
  return [
    route('Scoreboard', 'scoreboard', '/scoreboard'),
    route('Standings', 'standings', '/standings'),
    route('Schedule', 'when', '/schedule'),
    // The planner (D10): my roster over the coming weeks, by position.
    ...(real ? [route('Plan ahead', 'when', '/plan')] : []),
    route('Playoffs', 'podium', '/schedule?tab=playoffs'),
    // My Team's tools (B-400), found here by name like the rest.
    // Who on no team has scored lately (E1, PLAN-2026-10-27-worth-a-look).
    route('Worth a look', 'flame', '/worth-a-look'),
    route('Waivers', 'waivers', '/waivers'),
    route('Trades', 'trade', '/trades'),
    // EVERY ADD, DROP, CLAIM AND TRADE, newest first: the transaction log,
    // which was a segment inside Waivers that nobody looking for "what has
    // everyone been doing" would open (the audit of 2026-10-17).
    route('League activity', 'list', '/waivers?show=log'),
    route('News', 'news', '/news'),
    // Pick'em and the season wrapped, found by name too (the audit of
    // 2026-10-20): the front page's sections, and missing from this list.
    ...(real
      ? [route('Pick’em', 'favorite', '/pickem'), route('Your season', 'trophy', '/wrapped')]
      : []),
    route('Recap', 'recap', '/recap'),
    route('Awards', 'trophy', '/awards'),
    route('History', 'history', '/history'),
    route('Draft recap', 'draft', '/draft-recap'),
    route('Rules', 'rules', '/rules'),
    // Who runs each team, and — for the commissioner — replacing one (J4).
    route('Managers', 'people', '/managers'),
    route('Commissioner log', 'veto', '/audit'),
    ...settings,
    chat,
  ];
}

/** The paths under a league that belong to the League tab, for its active state. */
export const LEAGUE_TAB_PATHS: readonly string[] = [
  '/league',
  '/teams',
  '/scoreboard',
  '/standings',
  '/schedule',
  '/plan',
  '/worth-a-look',
  '/news',
  '/waivers',
  '/trades',
  '/awards',
  '/history',
  '/recap',
  '/draft-recap',
  '/rules',
  '/audit',
  '/managers',
  '/settings',
];
